import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Search, Layers, Briefcase, CreditCard, Settings, Home, LogOut, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Spinner from './Spinner';

interface NavItem {
  name: string;
  icon: React.ReactNode;
  path?: string;
  children?: { name: string; path: string }[];
}

const navItems: NavItem[] = [
  { name: 'Home', icon: <Home size={20} />, path: '/' },
  {
    name: 'Diagnostics',
    icon: <Search size={20} />,
    children: [
        { name: 'Overview', path: '/diagnostics/overview' },
        { name: 'Website Diagnostic Tool', path: '/diagnostics/website-tool' },
        { name: 'Marketing Diagnostic Tool', path: '/diagnostics/marketing-tool' },
        { name: 'Business Health & Strategy Assessment', path: '/diagnostics/business-assessment' },
    ],
  },

  { name: 'Website', icon: <Layers size={20} />, path: '/foundation' },
  {
    name: 'Job Board',
    icon: <Briefcase size={20} />,
    children: [
      { name: 'Overview', path: '/job-board/overview' },
      { name: 'Job Listings', path: '/job-board/jobs' },
      { name: 'Applicant Management', path: '/job-board/applicants/overview' },
    ],
  },
  {
    name: 'Billing & Proposals',
    icon: <CreditCard size={20} />,
    children: [
      { name: 'Invoices', path: '/invoices' },
      { name: 'Proposals', path: '/proposals' },
    ],
  },
  { name: 'Settings', icon: <Settings size={20} />, path: '/settings' },
];

export default function Sidebar() {
  const [isCollapsed, setIsCollapsed] = useState(true); // Default collapsed on small screens
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);
  
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const isActive = (path: string) => 
    location.pathname === path || (path !== '/' && location.pathname.startsWith(path + '/'));

  useEffect(() => {
    // Check for token in localStorage to verify session
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  const toggleAccordion = (name: string) => {
    // If sidebar is collapsed, expand it when opening an accordion
    if (isCollapsed) {
      setIsCollapsed(false);
      setOpenAccordion(name);
      return;
    }
    setOpenAccordion(openAccordion === name ? null : name);
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <>
      {/* Mobile Hamburger Trigger - Only visible when sidebar is hidden */}
      {isCollapsed && (
        <button
          onClick={() => setIsCollapsed(false)}
          className="md:hidden fixed top-4 left-4 z-50 p-2.5 bg-white border border-slate-200 shadow-lg rounded-xl text-slate-600 hover:text-blue-600 transition-all active:scale-95"
          aria-label="Open sidebar"
        >
          <Menu size={24} />
        </button>
      )}

      {/* Sidebar Aside */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 bg-slate-900 text-white flex flex-col transition-all duration-300 ease-in-out md:sticky md:top-0 md:h-screen ${
          isCollapsed 
            ? '-translate-x-full md:translate-x-0 md:w-20' 
            : 'translate-x-0 w-64 shadow-2xl md:shadow-none'
        }`}
      >
        {/* Header: Logo and Toggle Buttons */}
        <div className="flex items-center justify-between h-20 px-4 border-b border-slate-800 flex-shrink-0">
          {!isCollapsed && (
            <div className="flex items-center gap-2 animate-fade-in">
              <div className="w-9 h-9 bg-gradient-to-br from-blue-400 to-indigo-600 rounded-lg flex items-center justify-center font-bold text-white shadow-inner">
                S
              </div>
              <span className="font-bold text-xl tracking-tight bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">
                Strivre
              </span>
            </div>
          )}
          
          {/* Collapse/Expand for Desktop */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="hidden md:flex p-2 hover:bg-slate-800 text-slate-400 hover:text-white rounded-lg transition-colors focus:ring-2 focus:ring-blue-500 outline-none"
            aria-label="Toggle sidebar width"
          >
            {isCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
          </button>

          {/* Close for Mobile */}
          <button
            onClick={() => setIsCollapsed(true)}
            className="md:hidden p-2 hover:bg-slate-800 text-slate-400 hover:text-white rounded-lg transition-colors"
            aria-label="Close sidebar"
          >
            <X size={24} />
          </button>
        </div>

        {/* Navigation Links */}
        <nav className="flex-1 mt-4 px-3 space-y-1.5 overflow-y-auto scrollbar-hide">
          {navItems.map((item) =>
            item.children ? (
              <div key={item.name}>
                <button
                  onClick={() => toggleAccordion(item.name)}
                  className={`flex items-center gap-4 px-4 py-3 w-full rounded-lg transition-all duration-200 group text-left ${
                    openAccordion === item.name
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/40'
                      : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <span 
                    className="flex-shrink-0 text-current" 
                    aria-hidden="true"
                    title={isCollapsed ? item.name : undefined}
                  >
                    {item.icon}
                  </span>
                  {!isCollapsed && (
                    <div className="flex-1 flex items-center justify-between">
                      <span className="font-medium whitespace-nowrap">{item.name}</span>
                      <ChevronDown size={16} className={`transition-transform duration-200 ${openAccordion === item.name ? 'rotate-180' : ''}`} />
                    </div>
                  )}
                </button>
                {/* Accordion children */}
                {openAccordion === item.name && !isCollapsed && (
                  <div className="ml-10 mt-2 space-y-1 text-left">
                    {item.children.map((child) => (
                      <Link
                        key={child.name}
                        to={child.path}
                        className={`block px-2 py-2 rounded-lg text-sm transition-colors ${
                          isActive(child.path)
                            ? 'bg-blue-500 text-white'
                            : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                        }`}
                      >
                        {child.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={item.name}
                to={item.path!}
                className={`flex items-center gap-4 px-4 py-3 rounded-lg transition-all duration-200 group ${
                  isActive(item.path!)
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/40'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <span 
                  className="flex-shrink-0 text-current" 
                  aria-hidden="true"
                  title={isCollapsed ? item.name : undefined}
                >
                  {item.icon}
                </span>
                {!isCollapsed && <span className="font-medium whitespace-nowrap">{item.name}</span>}
              </Link>
            )
          )}

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            disabled={isLoggingOut}
            className="flex items-center gap-4 px-4 py-3 w-full rounded-lg transition-all duration-200 text-red-400 hover:bg-red-950/30 hover:text-red-300 disabled:opacity-50 group"
            aria-label="Log out"
          >
            <span className="flex-shrink-0" title={isCollapsed ? 'Logout' : undefined}>
              {isLoggingOut ? (
                <Spinner size="h-5 w-5" className="text-red-400" />
              ) : (
                <LogOut size={20} />
              )}
            </span>
            {!isCollapsed && (
              <span className="font-medium whitespace-nowrap">
                {isLoggingOut ? 'Logging out...' : 'Logout'}
              </span>
            )}
          </button>
        </nav>

        {/* Footer */}
        {!isCollapsed && (
          <div className="p-4 border-t border-slate-800 text-slate-400 text-xs">
            <p>© 2026 Strivre</p>
            <p className="mt-1">v1.0.0</p>
          </div>
        )}
      </aside>

      {/* Overlay for Mobile */}
      {!isCollapsed && (
        <div
          className="fixed inset-0 bg-black/50 md:hidden z-30"
          onClick={() => setIsCollapsed(true)}
        />
      )}
    </>
  );
}
